import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import { countries } from "countries-list";
import moment from "moment";
import ReactCodeInput from "react-code-input";
import ButtonContained from "components/ButtonContained";
import ButtonIconOnly from "components/ButtonIconOnly";
import Icon from "components/Icon";

import { userSignOut, userSignIn } from "actions/Auth";
import { setLoginPerformance, setErrorDetails, setGeneralLoader } from "actions/App";
import { toastManager } from "components/ToastNotification";

const LoginPin = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const generalLoader = useSelector((state) => state.app.generalLoader);
  const loginConfirm = useSelector((state) => state.auth.loginConfirm);
  const loginPerformance = useSelector((state) => state.app.loginPerformance);
  const errorDetails = useSelector((state) => state.app.errorDetails);
  const [pinCode, setPinCode] = useState("");
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    document.title = "Pin | Smart Entry";
  }, []);

  useEffect(() => {
    return () => {
      // Runs on unmount
      dispatch(setErrorDetails(null));
    };
  }, []);

  useEffect(() => {
    if (errorDetails) {
      setHasError(true);
      setErrorMessage(errorDetails.description);
    } else {
      setHasError(false);
      setErrorMessage("");
    }
  }, [errorDetails]);

  const redirectBack = () => {
    toastManager.addToast(intl.formatMessage({id: "notifications.error.fail"}));
    history.push("/signin");
  }

  const login = (e) => {
    e.preventDefault();
  
    const sessionLoginConfirm = JSON.parse(sessionStorage.getItem("login_confirm"));
    const localLoginConfirm = JSON.parse(localStorage.getItem("login_confirm"));
    const initialPhoneLogin = JSON.parse(sessionStorage.getItem("initial_phone_login"));
  
    const loginData = sessionLoginConfirm || loginConfirm || localLoginConfirm;
    const country = Object.values(countries).filter(c => c.phone[0] == (confirm.countryCode) && c.name == confirm.countryName)[0] || countries["US"];
  
    if (!loginData) {
      return redirectBack();
    }
  
    const isPhoneLogin = loginData.phone !== "";
    const loginType = isPhoneLogin ? "Phone" : "Email";
    const emailOrPhone = isPhoneLogin ? loginData.phone : loginData.email;
  
    dispatch(setLoginPerformance({
      ...loginPerformance,
      start_time: moment(),
      loginType,
      emailOrPhone,
    }));
  
    const payload = {
      country,
      phone: initialPhoneLogin
        ? initialPhoneLogin === "yes"
          ? loginData.phone
          : null
        : loginData.phone,
      email: initialPhoneLogin
        ? initialPhoneLogin === "no"
          ? loginData.email
          : null
        : loginData.email,
      password: pinCode,
      phoneLogin: isPhoneLogin,
      countryCode: loginData.countryCode,
      deviceUUID: loginData.deviceUUID,
    };
  
    dispatch(setGeneralLoader({button: true, text: intl.formatMessage({ id: "pages.appInstructions.loggingIn" })}));
    dispatch(userSignIn(payload));
  };

  const clearError = () => {
    setHasError(false);
    setErrorMessage("");
  }

  return (
    <div className="h-full flex flex-col">
      <label className="flex justify-between items-center h-10">
        <h5 className="tw-heading-5 font-bold text-black">
          {intl.formatMessage({ id: "auth.reset.header.Enterpin" })}
        </h5>
      </label>

      <div className="flex flex-col justify-between">
        <form onSubmit={(e) => login(e)}>
          <div className="mt-6 mb-16 w-full">
            <div className="flex justify-between">
              <ReactCodeInput
                id="Pin-Page-Pin-Code-Input"
                data-cy="Pin-Page-Pin-Code-Input"
                fields={6}
                inputMode="numeric"
                onChange={(value) => {
                  if (hasError) {
                    clearError();
                  }
                  setPinCode(value);
                }}
                value={pinCode}
                inputStyle={{
                  outline: "none",
                  fontFamily: "Outfit",
                  fontSize: "16px",
                  fontWeight: "bold",
                  marginRight: 8,
                  textAlign: "center",
                  border: "1px solid #999999",
                  borderRadius: 4,
                  color: "black",
                  height: "40px",
                  width: "40px"
                }}
              />
              {generalLoader && generalLoader.button ? <ButtonIconOnly
                id="Pin-Page-Continue-Button-Disabled"
                dataCy="Pin-Page-Continue-Button-Disabled"
                icon="loader20"
                size={20}
                type="alt"
                iconStyle="animate-spin [animation-duration:2000ms]"
              />
              : <ButtonIconOnly
                id="Pin-Page-Continue-Button"
                dataCy="Pin-Page-Continue-Button"
                icon="arrowRight"
                size={20}
                type="alt"
                buttonType="submit"
              />}
            </div>
            {hasError && (
              <div className="absolute flex items-center gap-2 text-gray-800 mt-1 ml-2">
                <Icon name="circleAlert" size={20} fillColor="fill-[var(--color-alert)]" className="text-[var(--color-white)]" />
                <span className="tw-paragraph-sm font-semibold">{errorMessage}</span>
              </div>
            )}
          </div>
          <div className="flex justify-start">
            <ButtonContained
              id="Pin-Page-Back-Button"
              dataCy="Pin-Page-Back-Button"
              text={intl.formatMessage({id: "auth.reset.back"})}
              type="default"
              onClick={(e) => {e.preventDefault(); dispatch(userSignOut())}}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPin;